import { useCallback, useEffect, useState, type ReactNode } from "react";

import { OnboardingShell } from "@/features/onboarding/OnboardingShell";
import {
  NotificationsSlide,
  useNotificationsSlideAction,
} from "@/features/onboarding/slides/NotificationsSlide";
import { PaywallSlide } from "@/features/onboarding/slides/PaywallSlide";
import { TrialReassuranceSlide } from "@/features/onboarding/slides/TrialReassuranceSlide";
import { useScreen0Language } from "@/features/language";
import { getTranslations } from "@/i18n";
import type { PremiumTabId, PremiumTabPath } from "@/lib/monetization/premiumTabs";
import { premiumTabPathToId } from "@/lib/monetization/premiumTabs";
import {
  TabPaywallAnalyticsProvider,
  useTabPaywallAnalyticsContext,
} from "@/lib/monetization/TabPaywallAnalyticsContext";
import { useTabPaywallAnalytics } from "@/lib/monetization/useTabPaywallAnalytics";
import { triggerOnboardingAdvanceHaptic } from "@/lib/native/haptics";

const TOTAL_STEPS = 3;

type PremiumTabGateFlowProps = {
  requestedTab: PremiumTabPath;
  onDismiss: () => void;
  onUnlock: () => void;
};

function PremiumTabGateFlowInner({ onDismiss, onUnlock }: Omit<PremiumTabGateFlowProps, "requestedTab">) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const analytics = useTabPaywallAnalyticsContext();
  const [step, setStep] = useState(1);

  useEffect(() => {
    analytics.trackScreenView(step);
  }, [analytics, step]);

  const dismissGate = useCallback(
    (exitReason: string) => {
      analytics.trackGateDismiss(exitReason, step);
      onDismiss();
    },
    [analytics, onDismiss, step],
  );

  const advance = useCallback(() => {
    triggerOnboardingAdvanceHaptic();
    analytics.trackCtaClick(step, "primary");
    setStep((current) => Math.min(current + 1, TOTAL_STEPS));
  }, [analytics, step]);

  const handlePaywallUnlock = useCallback(() => {
    analytics.trackGateUnlock();
    onUnlock();
  }, [analytics, onUnlock]);

  const {
    handleAllowCta,
    handleMaybeLater,
    ctaLabel: notificationsCtaLabel,
    secondaryCtaLabel: notificationsSecondaryCtaLabel,
    notificationsBusy,
    notificationsDenied,
  } = useNotificationsSlideAction(advance, { step: 2 });

  let shellCta: { label: string; onClick: () => void; busy?: boolean } | null = null;
  let shellSecondaryCta: { label: string; onClick: () => void } | null = null;

  if (step === 1) {
    shellCta = { label: t.onboarding.continue, onClick: advance };
  } else if (step === 2) {
    shellCta = {
      label: notificationsCtaLabel,
      onClick: () => void handleAllowCta(),
      busy: notificationsBusy,
    };
    shellSecondaryCta = {
      label: notificationsSecondaryCtaLabel,
      onClick: () => {
        analytics.trackCtaClick(step, "secondary");
        handleMaybeLater();
      },
    };
  } else if (step === 3) {
    shellCta = null;
  }

  let content: ReactNode = null;

  if (step === 1) {
    content = <TrialReassuranceSlide />;
  } else if (step === 2) {
    content = <NotificationsSlide denied={notificationsDenied} />;
  } else if (step === 3) {
    content = (
      <PaywallSlide
        analyticsStep={3}
        onAdvance={handlePaywallUnlock}
        onDismiss={() => dismissGate("close_x")}
      />
    );
  }

  const isPaywallStep = step === 3;

  return (
    <OnboardingShell
      contentKey={step}
      showProgress={!isPaywallStep}
      progressTotal={TOTAL_STEPS}
      progressCurrent={step}
      ctaLabel={shellCta?.label}
      ctaBusy={shellCta?.busy}
      onCta={shellCta?.onClick}
      secondaryCtaLabel={shellSecondaryCta?.label}
      onSecondaryCta={shellSecondaryCta?.onClick}
      hideHeader={isPaywallStep}
      hideFooter={isPaywallStep}
      disableContentScroll={isPaywallStep}
      flushBottom={isPaywallStep}
      flushHorizontal={isPaywallStep}
    >
      {content}
    </OnboardingShell>
  );
}

export function PremiumTabGateFlow({ requestedTab, onDismiss, onUnlock }: PremiumTabGateFlowProps) {
  const tabId = premiumTabPathToId(requestedTab);
  const analytics = useTabPaywallAnalytics(tabId);

  return (
    <TabPaywallAnalyticsProvider value={analytics}>
      <PremiumTabGateFlowInner onDismiss={onDismiss} onUnlock={onUnlock} />
    </TabPaywallAnalyticsProvider>
  );
}

export type { PremiumTabId };
